import { getCoffreSuiteApiBase } from "@/auth/paths";
import { mailAuthHeaders, readStoredSuiteUserId } from "@/auth/session";
import {
  dropSuiteFolderListCache,
  loadSuiteFolderListCached,
  prefetchSuiteFolderList,
  readSuiteFolderListCache,
} from "@/lib/suite/suiteFolderListCache";

const COFFRE_LIST_CACHE_NS = "coffre-list";

export type CoffreListZone = "vault" | "ged" | "picker" | "trash" | "quarantine" | "shared";

export type CoffreEntry = {
  name: string;
  path: string;
  kind: "folder" | "file" | "shortcut";
  size?: number | null;
};

export type CoffreListResponse = {
  success: boolean;
  path: string;
  zone?: CoffreListZone;
  entries?: CoffreEntry[];
  message?: string;
};

export type CoffreStatusResponse = {
  success: boolean;
  has_vault?: boolean;
  server_unlocked?: boolean;
  idle_seconds_remaining?: number;
  session_extended?: boolean;
  session_idle_seconds?: number;
  message?: string;
  error?: string;
};

export type CoffreSuiteStatus = {
  ok: boolean;
  hasVault: boolean;
  unlocked: boolean;
  idleSecondsRemaining: number;
  sessionIdleSeconds: number;
  sessionExtended: boolean;
  authError?: string;
  message?: string;
};

type CoffreApiResult<T = Record<string, unknown>> = {
  ok: boolean;
  status: number;
  data: T;
};

function coffreStatusQuery(): string {
  const qs = new URLSearchParams();
  const uid = readStoredSuiteUserId();
  if (uid) qs.set("suite_user_id", uid);
  const q = qs.toString();
  return q ? `?${q}` : "";
}

async function coffreSuiteApiFetch<T = Record<string, unknown>>(
  path: string,
  init: RequestInit = {}
): Promise<CoffreApiResult<T>> {
  const url = `${getCoffreSuiteApiBase()}/${path.replace(/^\//, "")}`;
  let res: Response;
  try {
    res = await fetch(url, {
      credentials: "include",
      cache: "no-store",
      ...init,
      headers: {
        ...mailAuthHeaders(),
        ...(init.headers as Record<string, string> | undefined),
      },
    });
  } catch {
    return {
      ok: false,
      status: 0,
      data: {} as T,
    };
  }

  const data = (await res.json().catch(() => ({}))) as T;
  return {
    ok: res.ok && (data as { success?: boolean }).success !== false,
    status: res.status,
    data,
  };
}

function parseServerUnlocked(value: unknown): boolean {
  return value === true || value === 1 || value === "1";
}

export async function fetchCoffreSuiteStatus(): Promise<CoffreSuiteStatus> {
  const res = await coffreSuiteApiFetch<CoffreStatusResponse>(
    `coffre-status.php${coffreStatusQuery()}`
  );
  if (!res.ok) {
    const authError =
      typeof res.data.error === "string" ? res.data.error : res.status === 401 ? "session_required" : undefined;
    return {
      ok: false,
      hasVault: false,
      unlocked: false,
      idleSecondsRemaining: 0,
      sessionIdleSeconds: 300,
      sessionExtended: false,
      authError,
      message: res.data.message ?? res.data.error ?? "Coffre indisponible.",
    };
  }
  const hasVault = res.data.has_vault === true;
  const unlocked = parseServerUnlocked(res.data.server_unlocked);
  return {
    ok: true,
    hasVault,
    unlocked,
    idleSecondsRemaining: Math.max(0, Number(res.data.idle_seconds_remaining) || 0),
    sessionIdleSeconds: Math.max(300, Number(res.data.session_idle_seconds) || 300),
    sessionExtended: res.data.session_extended === true,
    message: res.data.message,
  };
}

/** Boot / refresh : status + prefetch racine picker si déverrouillé. */
export async function warmCoffreSuiteConnection(): Promise<CoffreSuiteStatus> {
  const status = await fetchCoffreSuiteStatus();
  if (status.ok && status.unlocked && status.hasVault) {
    prefetchCoffrePickerList("");
  }
  return status;
}

export async function fetchCoffreList(
  path = "",
  zone: CoffreListZone = "vault"
): Promise<{ path: string; entries: CoffreEntry[]; error?: string }> {
  const qs = new URLSearchParams();
  if (path) qs.set("path", path);
  if (zone) qs.set("zone", zone);
  if (zone === "picker") qs.set("folders_only", "1");
  const query = qs.toString();
  const res = await coffreSuiteApiFetch<CoffreListResponse>(
    query ? `coffre-list.php?${query}` : "coffre-list.php"
  );
  if (!res.ok) {
    const data = res.data as CoffreListResponse & { error?: string };
    const locked =
      res.status === 403 &&
      (data.error === "coffre_locked" ||
        data.error === "coffre_session_expired" ||
        (data.message ?? "").toLowerCase().includes("déverrouill"));
    return {
      path,
      entries: [],
      error:
        data.message ??
        (locked
          ? "Le Coffre est fermé. Saisissez votre mot de passe maître pour enregistrer un fichier."
          : "Impossible de lire le Coffre."),
    };
  }
  return {
    path: res.data.path ?? path,
    entries: res.data.entries ?? [],
  };
}

function coffreListCacheKey(path: string, zone: CoffreListZone): string {
  return `${zone}:${path}`;
}

/** Précharge la racine Coffre (picker) avant ouverture de la modale destination. */
export function prefetchCoffrePickerList(path = ""): void {
  prefetchSuiteFolderList(COFFRE_LIST_CACHE_NS, coffreListCacheKey(path, "picker"), () =>
    fetchCoffreList(path, "picker")
  );
}

/** Précharge une zone (vault/ged) — attach compose a besoin des fichiers. */
export function prefetchCoffreList(path = "", zone: CoffreListZone = "vault"): void {
  prefetchSuiteFolderList(COFFRE_LIST_CACHE_NS, coffreListCacheKey(path, zone), () =>
    fetchCoffreList(path, zone)
  );
}

export async function fetchCoffreListCached(
  path = "",
  zone: CoffreListZone = "picker",
  signal?: AbortSignal
): Promise<{ path: string; entries: CoffreEntry[]; error?: string }> {
  const key = coffreListCacheKey(path, zone);
  const cached = readSuiteFolderListCache<{ path: string; entries: CoffreEntry[]; error?: string }>(
    COFFRE_LIST_CACHE_NS,
    key
  );
  if (cached != null) return cached;

  return loadSuiteFolderListCached(COFFRE_LIST_CACHE_NS, key, () => fetchCoffreList(path, zone), {
    signal,
  });
}

export async function fetchCoffreFileBlob(
  path: string,
  options: { zone?: CoffreListZone } = {}
): Promise<{ blob: Blob | null; filename: string; error?: string }> {
  const qs = new URLSearchParams();
  qs.set("path", path);
  if (options.zone) qs.set("zone", options.zone);
  qs.set("stream", "1");
  const url = `${getCoffreSuiteApiBase()}/coffre-download.php?${qs.toString()}`;
  try {
    const res = await fetch(url, {
      credentials: "include",
      headers: mailAuthHeaders(),
    });
    if (!res.ok) {
      let message = `Erreur HTTP ${res.status}`;
      try {
        const body = (await res.json()) as { message?: string };
        if (body.message) message = body.message;
      } catch {
        /* binaire */
      }
      return { blob: null, filename: path.split("/").pop() ?? "fichier", error: message };
    }
    const blob = await res.blob();
    const filenameFallback = path.split("/").pop() ?? "fichier";
    if (blob.size <= 0) {
      return {
        blob: null,
        filename: filenameFallback,
        error: "Fichier Coffre vide (0 o). Réessayez ou joignez-le depuis l’ordinateur.",
      };
    }
    const disposition = res.headers.get("Content-Disposition") ?? "";
    const match = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(disposition);
    const filename = match?.[1]
      ? decodeURIComponent(match[1].replace(/"/g, ""))
      : filenameFallback;
    return { blob, filename };
  } catch (err) {
    return {
      blob: null,
      filename: path.split("/").pop() ?? "fichier",
      error: err instanceof Error ? err.message : "Téléchargement Coffre impossible.",
    };
  }
}

/** Oublie la liste en cache d’un dossier après une création. */
export function invalidateCoffreListCache(path = ""): void {
  for (const zone of ["picker", "vault", "ged"] as const) {
    dropSuiteFolderListCache(COFFRE_LIST_CACHE_NS, coffreListCacheKey(path, zone));
  }
}

export async function createCoffreFolder(
  parentPath: string,
  name: string
): Promise<{ ok: boolean; path?: string; message?: string }> {
  const res = await coffreSuiteApiFetch<{
    success: boolean;
    path?: string;
    message?: string;
    error?: string;
  }>("coffre-mkdir.php", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ parent_path: parentPath, name, zone: "vault" }),
  });
  if (!res.ok) {
    return {
      ok: false,
      message: res.data.message ?? "Impossible de créer le dossier.",
    };
  }
  const trimmed = name.trim();
  const created =
    (typeof res.data.path === "string" && res.data.path.trim()) ||
    (parentPath ? `${parentPath}/${trimmed}` : trimmed);
  invalidateCoffreListCache(parentPath);
  invalidateCoffreListCache(created);
  return { ok: true, path: created, message: res.data.message };
}

export async function uploadCoffreFile(params: {
  file: Blob;
  filename: string;
  path: string;
  zone?: CoffreListZone;
}) {
  const form = new FormData();
  form.append("path", params.path);
  form.append("file", params.file, params.filename);
  if (params.zone) form.append("zone", params.zone);

  return coffreSuiteApiFetch<{ success: boolean; path?: string; name?: string; message?: string; error?: string }>(
    "coffre-upload.php",
    { method: "POST", body: form }
  );
}
